import { Link } from "@tanstack/react-router";
import type { AnchorHTMLAttributes, ReactNode } from "react";

interface SmartLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  catalog: string;
  currentSlug: string;
  knownSlugs: Set<string>;
}

/**
 * Markdown `<a>` renderer.
 *
 * - `#anchor`     → unchanged (same-page heading link)
 * - absolute URL  → external, opens in new tab with `↗` icon
 * - starts with `/` → unchanged (intentional app path)
 * - relative       → resolved against current slug; if the resolved slug
 *                    exists in the catalog's document list → TanStack Link.
 *                    If not → rendered as a muted, strikethrough span so the
 *                    reader can see the link is broken.
 */
export function SmartLink({
  catalog,
  currentSlug,
  knownSlugs,
  href,
  children,
  ...rest
}: SmartLinkProps): ReactNode {
  if (!href) return <span {...rest}>{children}</span>;

  // Fragment-only link.
  if (href.startsWith("#")) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  }

  // Absolute external URL.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) {
    const isHttp = /^https?:/i.test(href);
    return (
      <a
        href={href}
        target={isHttp ? "_blank" : undefined}
        rel={isHttp ? "noopener noreferrer" : undefined}
        {...rest}
      >
        {children}
        {isHttp && <ExternalIcon />}
      </a>
    );
  }

  // Protocol-relative (`//host/...`) → treat as external.
  if (href.startsWith("//")) {
    return (
      <a href={`https:${href}`} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
        <ExternalIcon />
      </a>
    );
  }

  // App-absolute (`/foo`) — leave as-is.
  if (href.startsWith("/")) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  }

  // Relative — resolve against current slug.
  const [pathPart = "", hashPart] = href.split("#", 2);
  const resolved = resolveRelative(currentSlug, pathPart);
  const cleaned = stripMdExt(resolved);

  if (cleaned && knownSlugs.has(cleaned)) {
    return (
      <Link
        to="/catalogs/$name/$"
        params={{ name: catalog, _splat: cleaned }}
        {...(hashPart ? { hash: hashPart } : {})}
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </Link>
    );
  }

  // Broken relative link — mark visibly so authors can spot it.
  return (
    <span
      className="text-[var(--fg-muted)] line-through decoration-[var(--danger)]/60 cursor-not-allowed"
      title={`Broken link: ${href} (no document at ${cleaned || resolved})`}
      {...rest}
    >
      {children}
      <BrokenIcon />
    </span>
  );
}

function stripMdExt(path: string): string {
  return path
    .replace(/\.mdx?$/i, "")
    .replace(/\/index$/i, "")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "");
}

function resolveRelative(currentSlug: string, rel: string): string {
  if (!rel) return currentSlug;
  // Walk directory of current slug.
  const baseParts = currentSlug.split("/").slice(0, -1);
  const relParts = rel.split("/");
  for (const p of relParts) {
    if (p === "" || p === ".") continue;
    if (p === "..") baseParts.pop();
    else baseParts.push(p);
  }
  return baseParts.join("/");
}

function ExternalIcon() {
  return (
    <svg
      aria-hidden
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ml-0.5 inline-block -translate-y-px opacity-70"
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

function BrokenIcon() {
  return (
    <svg
      aria-hidden
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ml-0.5 inline-block -translate-y-px text-[var(--danger)]/70"
    >
      <path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
      <path d="m3 3 18 18" />
    </svg>
  );
}
